import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Inbox, CalendarDays, CalendarCheck, CalendarX, User, Clock, Wallet, Star, Loader2, Crown, Check, X, Sun, Sunset, Moon, Info, Plus, Trash2, MessageSquare, Zap, ShieldCheck, LogOut, ImagePlus, BadgePoundSterling, FileText, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { AchievementsGrid } from "@/components/AchievementBadges";
import PropertyBrief from "@/components/PropertyBrief";
import TurnoverJobPanel from "@/components/TurnoverJobPanel";
import AccessLockedNote from "@/components/AccessLockedNote";
import NoShowCases from "@/components/NoShowPanel";
import RestrictedBanner from "@/components/RestrictedBanner";
import DbsPanel from "@/components/DbsPanel";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import PremiumPanel from "@/components/PremiumPanel";
import AccountSecurity from "@/components/AccountSecurity";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import ServicePricingEditor from "@/components/ServicePricingEditor";
import QuotesPanel from "@/components/QuotesPanel";
import PayoutsPanel from "@/components/PayoutsPanel";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SLOTS = [
  { key: "morning", label: "Morning", sub: "8am – 12pm", icon: Sun },
  { key: "afternoon", label: "Afternoon", sub: "12pm – 5pm", icon: Sunset },
  { key: "evening", label: "Evening", sub: "5pm – 9pm", icon: Moon },
];

export default function CleanerDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [active, setActive] = useState(new URLSearchParams(window.location.search).get("tab") || "overview");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pricingDraft, setPricingDraft] = useState({});
  const [blockDate, setBlockDate] = useState("");

  const load = async () => {
    const [s, b, p, sv] = await Promise.all([
      api.get("/dashboard/stats"), api.get("/bookings/mine"),
      api.get("/cleaners/me"), api.get("/services"),
    ]);
    setStats(s.data); setBookings(b.data.bookings); setProfile(p.data); setServices(sv.data.services);
    setPricingDraft(p.data.service_pricing || {});
    if (p.data && p.data.onboarded === false) navigate("/cleaner/onboarding", { replace: true });
  };
  useEffect(() => { load(); }, []);

  const act = async (path, id) => {
    try { await api.post(`/bookings/${path}`, { booking_id: id }); toast.success(`Booking ${path}ed`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { availability, ...rest } = profile;
      await api.put("/cleaners/me", { ...rest, availability });
      toast.success("Profile updated"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upd = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const [photoBusy, setPhotoBusy] = useState(false);
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/cleaners/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("photo_url", data.photo_url);
      toast.success("Profile photo updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPhotoBusy(false); e.target.value = ""; }
  };
  const updAvail = (k, v) => setProfile((p) => ({ ...p, availability: { ...p.availability, [k]: v } }));
  const toggleDay = (d) => {
    const wd = profile.availability.working_days.includes(d)
      ? profile.availability.working_days.filter((x) => x !== d)
      : [...profile.availability.working_days, d];
    updAvail("working_days", wd);
  };
  const toggleSlot = (k) => {
    const ts = { ...(profile.availability.time_slots || {}), [k]: !(profile.availability.time_slots || {})[k] };
    updAvail("time_slots", ts);
  };
  const toggleService = (slug) => {
    const list = profile.services.includes(slug) ? profile.services.filter((s) => s !== slug) : [...profile.services, slug];
    upd("services", list);
  };

  const tabs = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "requests", label: "Requests", icon: Inbox },
    { key: "upcoming", label: "Upcoming", icon: CalendarDays },
    { key: "completed", label: "Completed", icon: CalendarCheck },
    { key: "profile", label: "Profile", icon: User },
    { key: "pricing", label: "Pricing", icon: BadgePoundSterling },
    { key: "quotes", label: "Quotes", icon: FileText },
    { key: "dbs", label: "DBS & Verification", icon: ShieldCheck },
    { key: "availability", label: "Availability", icon: Clock },
    { key: "reviews", label: "Reviews", icon: Star },
    { key: "earnings", label: "Earnings", icon: Wallet },
    { key: "payouts", label: "Payouts", icon: CreditCard },
  ];

  const requests = bookings.filter((b) => b.status === "pending");
  const upcoming = bookings.filter((b) => ["confirmed", "awaiting_approval"].includes(b.status));
  const completed = bookings.filter((b) => b.status === "completed");
  const pastOther = bookings.filter((b) => ["cancelled", "declined"].includes(b.status));

  const addBlockedDate = () => {
    if (!blockDate) return;
    const list = profile.availability.blocked_dates || [];
    if (!list.includes(blockDate)) updAvail("blocked_dates", [...list, blockDate].sort());
    setBlockDate("");
  };
  const removeBlockedDate = (d) => updAvail("blocked_dates", (profile.availability.blocked_dates || []).filter((x) => x !== d));

  return (
    <DashboardShell title="Cleaner Dashboard" tabs={tabs} active={active} onTab={setActive}>
      {!stats || !profile ? <Loader2 className="h-6 w-6 animate-spin text-accent" /> : (
        <>
          {active === "overview" && (
            <div className="space-y-6" data-testid="cleaner-overview">
              {profile.onboarded === false && (
                <div className="p-4 rounded-2xl bg-secondary/60 border border-border text-sm" data-testid="profile-setup-note">
                  Finish your profile — add your services, pricing, service area and availability so
                  customers can find and book you.
                </div>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Available balance" value={`£${stats.available_balance?.toFixed(2)}`} />
                <Stat label="Pending requests" value={stats.pending_requests} />
                <Stat label="Upcoming jobs" value={stats.upcoming_jobs} />
                <Stat label="Rating" value={stats.rating || "—"} />
              </div>
              <PremiumPanel onChange={load} />

              <div className="bg-card rounded-2xl border border-primary/5 p-6" data-testid="cleaner-achievements">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-display font-semibold">Your achievements</h3>
                  {profile.reliability_score != null && (
                    <span className="text-sm text-muted-foreground">Reliability score: <strong className="text-accent">{profile.reliability_score}%</strong></span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">Earned automatically from your verified bookings and ratings — never manually assigned.</p>
                {(() => {
                  const NEXT = [10, 50, 100];
                  const jobs = profile.completed_jobs || 0;
                  const target = NEXT.find((n) => n > jobs);
                  if (!target) return <p className="mt-4 text-sm text-accent font-medium">🎉 All milestones achieved — you're a top performer!</p>;
                  return (
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>Next: {target} Cleans Completed</span><span>{jobs}/{target}</span></div>
                      <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-accent transition-all" style={{ width: `${Math.min(100, (jobs / target) * 100)}%` }} /></div>
                    </div>
                  );
                })()}
                <div className="mt-5"><AchievementsGrid earned={profile.badges} showLocked /></div>
              </div>
            </div>
          )}

          {active === "requests" && (
            <div className="space-y-4" data-testid="booking-requests">
              <RestrictedBanner />
              <NoShowCases role="cleaner" />
              {requests.length === 0 ? <p className="text-muted-foreground py-10 text-center">No pending requests right now.</p> :
                requests.map((b) => (
                  <div key={b.booking_id} className="bg-card rounded-2xl border border-primary/5 p-5">
                    <p className="font-display font-semibold">{b.service_name} · £{b.price_total.toFixed(2)}</p>
                    <p className="text-sm text-muted-foreground mt-1">{b.customer_name} · {b.date} at {b.start_time} · {b.duration_hours}h</p>
                    <p className="text-sm text-muted-foreground">{b.address?.line1}, {b.address?.city} {b.address?.postcode}</p>
                    {b.special_instructions && <p className="text-sm mt-2 italic">"{b.special_instructions}"</p>}
                    <PropertyBrief summary={b.property_summary} turnover={b.turnover} />
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => act("accept", b.booking_id)} className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors" data-testid={`accept-${b.booking_id}`}><Check className="h-4 w-4" /> Accept</button>
                      <button onClick={() => act("decline", b.booking_id)} className="inline-flex items-center gap-1 px-5 py-2 rounded-full border border-border text-sm font-medium hover:bg-secondary transition-colors" data-testid={`decline-${b.booking_id}`}><X className="h-4 w-4" /> Decline</button>
                      <button onClick={() => navigate("/messages", { state: { to: b.customer_id, name: b.customer_name } })} className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-secondary transition-colors" data-testid={`msg-customer-${b.booking_id}`}><MessageSquare className="h-4 w-4" /> Message</button>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {active === "upcoming" && (
            <div className="space-y-4" data-testid="cleaner-upcoming">
              {upcoming.length === 0 ? <p className="text-muted-foreground py-10 text-center">No upcoming jobs. Accepted bookings will appear here.</p> :
                upcoming.map((b) => (
                  <div key={b.booking_id} className="bg-card rounded-2xl border border-primary/5 p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <p className="font-display font-semibold">{b.service_name} · £{b.cleaner_earnings?.toFixed(2)}</p>
                        <p className="text-sm text-muted-foreground mt-1">{b.customer_name} · {b.date} at {b.start_time} · {b.duration_hours}h</p>
                        <p className="text-sm text-muted-foreground">{b.address?.line1}, {b.address?.city} {b.address?.postcode}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => navigate("/messages", { state: { to: b.customer_id, name: b.customer_name } })} className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-secondary transition-colors inline-flex items-center gap-1" data-testid={`msg-customer-${b.booking_id}`}><MessageSquare className="h-4 w-4" /> Message</button>
                        {!b.requires_host_approval && b.status === "confirmed" && (
                          <button onClick={() => act("complete", b.booking_id)} className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors whitespace-nowrap" data-testid={`complete-${b.booking_id}`}>Mark complete</button>
                        )}
                      </div>
                    </div>
                    {b.requires_host_approval && <TurnoverJobPanel booking={b} onDone={load} />}
                  </div>
                ))}
            </div>
          )}

          {active === "completed" && (
            <div className="space-y-6" data-testid="cleaner-completed">
              <div>
                <h3 className="font-display font-semibold mb-3">Completed jobs</h3>
                <div className="space-y-3">
                  {completed.length === 0 ? <p className="text-muted-foreground py-6 text-center">No completed jobs yet.</p> :
                    completed.map((b) => (
                      <div key={b.booking_id} className="bg-card rounded-2xl border border-primary/5 p-5">
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <p className="font-display font-semibold">{b.service_name} · £{b.cleaner_earnings?.toFixed(2)}</p>
                            <p className="text-sm text-muted-foreground mt-1">{b.customer_name} · {b.date} at {b.start_time}</p>
                            {b.property_summary?.name && <p className="text-xs text-accent font-medium mt-1">{b.property_summary.name}</p>}
                          </div>
                          <span className="inline-flex items-center gap-1 text-sm text-emerald-600 shrink-0"><Check className="h-4 w-4" /> Completed</span>
                        </div>
                        {b.requires_host_approval && <AccessLockedNote bookingId={b.booking_id} />}
                      </div>
                    ))}
                </div>
              </div>
              {pastOther.length > 0 && (
                <div>
                  <h3 className="font-display font-semibold mb-3 text-muted-foreground">Cancelled & declined</h3>
                  <div className="space-y-3">
                    {pastOther.map((b) => (
                      <div key={b.booking_id} className="bg-card rounded-2xl border border-primary/5 p-4 flex items-center justify-between gap-4 opacity-75">
                        <p className="text-sm">{b.service_name} · {b.customer_name} · {b.date}</p>
                        <span className="text-xs capitalize text-muted-foreground">{b.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {active === "profile" && (
            <div className="space-y-6 max-w-2xl">
            <div className="bg-card rounded-2xl border border-primary/5 p-6 space-y-5" data-testid="cleaner-profile-form">
              <div className="rounded-xl bg-secondary/40 p-4 text-sm" data-testid="cleaner-account-details">
                <p className="font-semibold">Account details</p>
                <p className="text-muted-foreground mt-1">{user?.name} · {user?.email} · signed in as a cleaner</p>
                <p className="text-muted-foreground">DBS: {profile.dbs?.label || (profile.dbs_verified ? "DBS Verified" : "DBS not provided")} · Confirmed no-shows: {profile.confirmed_no_shows || 0}{profile.reliability_score != null ? ` · Reliability ${profile.reliability_score}%` : ""}</p>
                <button onClick={logout} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-secondary transition-colors" data-testid="cleaner-logout-btn">
                  <LogOut className="h-4 w-4" /> Log out
                </button>
              </div>
              <div className="flex items-center gap-4">
                <img src={profile.photo_url || "https://via.placeholder.com/80?text=Photo"} alt="" className="h-20 w-20 rounded-2xl object-cover border border-border" data-testid="profile-photo-preview" />
                <div>
                  <p className="font-display font-semibold text-lg">{profile.name}</p>
                  <p className="text-sm text-muted-foreground">Add a clear photo of yourself — it's what customers see first.</p>
                  <label className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent text-white text-sm font-medium hover:bg-accent/90 transition-colors cursor-pointer" data-testid="profile-photo-upload-label">
                    {photoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                    {profile.photo_url ? "Change photo" : "Upload photo"}
                    <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="profile-photo-input" />
                  </label>
                </div>
              </div>
              <Field label="Type">
                <select value={profile.type} onChange={(e) => upd("type", e.target.value)} className="input"><option value="independent">Independent</option><option value="company">Company</option></select>
              </Field>
              <Field label="Biography"><textarea value={profile.bio || ""} onChange={(e) => upd("bio", e.target.value)} rows={4} className="input" data-testid="profile-bio" /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Years experience"><input type="number" value={profile.years_experience} onChange={(e) => upd("years_experience", parseInt(e.target.value))} className="input" /></Field>
                <Field label="Town"><input value={profile.town || ""} onChange={(e) => upd("town", e.target.value)} className="input" /></Field>
                <Field label="Photo URL"><input value={profile.photo_url || ""} onChange={(e) => upd("photo_url", e.target.value)} className="input" /></Field>
              </div>
              <div className="p-4 rounded-xl bg-secondary/60 border border-border text-sm" data-testid="profile-pricing-link">
                Your prices are set per service — open the <button onClick={() => setActive("pricing")} className="text-accent font-medium underline" data-testid="profile-goto-pricing">Pricing</button> tab to change them.
              </div>
              <Field label="Where you provide cleaning services">
                <LocationAutocomplete onSelect={(a) => upd("service_areas", (profile.service_areas || []).some((x) => x.place_id === a.place_id) ? profile.service_areas : [...(profile.service_areas || []), a])} testid="profile-area" />
                <div className="flex flex-wrap gap-2 mt-2" data-testid="profile-area-chips">
                  {(profile.service_areas || []).map((a) => (
                    <span key={a.place_id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-medium">
                      {a.label}
                      <button type="button" onClick={() => upd("service_areas", (profile.service_areas || []).filter((x) => x.place_id !== a.place_id))} className="hover:text-destructive" data-testid="profile-area-remove">×</button>
                    </span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Customers find you by town — no postcode lists or travel radius needed.</p>
              </Field>
              <Field label="Languages (comma separated)"><input value={(profile.languages || []).join(", ")} onChange={(e) => upd("languages", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className="input" /></Field>
              <Field label="Services offered">
                <div className="flex flex-wrap gap-2 mt-1">
                  {services.map((s) => (
                    <button key={s.slug} onClick={() => toggleService(s.slug)} className={`px-3 py-1.5 rounded-full text-sm border-2 transition-colors ${profile.services?.includes(s.slug) ? "border-accent bg-accent/5" : "border-border"}`}>{s.name}</button>
                  ))}
                </div>
              </Field>

              <div className="border-t border-border pt-5" data-testid="priority-settings">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-accent" />
                  <h4 className="font-semibold text-sm">Priority / urgent bookings</h4>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Optional. Charge more for last-minute and urgent jobs. Customers only see this if you switch it on.
                </p>
                <button onClick={() => upd("priority_enabled", !profile.priority_enabled)}
                  className={`mt-3 flex items-center gap-2.5 p-3 rounded-xl border-2 text-sm text-left w-full sm:w-auto transition-colors ${profile.priority_enabled ? "border-accent bg-accent/5" : "border-border hover:border-accent/40"}`}
                  data-testid="priority-toggle">
                  <span className={`h-5 w-5 rounded-md flex items-center justify-center border ${profile.priority_enabled ? "bg-accent border-accent text-white" : "border-border"}`}>
                    {profile.priority_enabled && <Check className="h-3.5 w-3.5" />}
                  </span>
                  I accept priority / urgent bookings
                </button>
                {profile.priority_enabled && (
                  <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    <Field label="Priority hourly rate (£)">
                      <input type="number" min="0" step="0.5" value={profile.priority_hourly_rate ?? ""}
                        onChange={(e) => upd("priority_hourly_rate", e.target.value === "" ? null : parseFloat(e.target.value))}
                        placeholder={`e.g. ${Math.round((profile.hourly_rate || 20) * 1.33)}`} className="input" data-testid="profile-priority-rate" />
                      <p className="text-xs text-muted-foreground mt-1">
                        Priority bookings are for urgent or short-notice jobs — we recommend setting this higher than your standard rate.
                      </p>
                    </Field>
                    <Field label="Priority availability notes">
                      <input value={profile.priority_notes || ""} onChange={(e) => upd("priority_notes", e.target.value)}
                        placeholder="e.g. Same-day if booked before 11am" className="input" data-testid="profile-priority-notes" />
                    </Field>
                  </div>
                )}
              </div>
              <button onClick={saveProfile} disabled={saving} className="px-6 py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors flex items-center gap-2 disabled:opacity-50" data-testid="save-profile-btn">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save profile</button>
            </div>
            <AccountSecurity />
            </div>
          )}

          {active === "dbs" && (
            <div data-testid="cleaner-dbs-tab"><DbsPanel /></div>
          )}

          {active === "availability" && (
            <div className="max-w-3xl space-y-5" data-testid="availability-form">
              <div className="flex gap-3 p-4 rounded-xl bg-accent/10 text-sm">
                <Info className="h-5 w-5 text-accent shrink-0" />
                <p>
                  Mark the real dates and times you're free — nothing repeats automatically, so your
                  calendar always shows customers exactly when you can work.
                </p>
              </div>
              <AvailabilityCalendar />
              <div className="bg-card rounded-2xl border border-primary/5 p-5 max-w-sm">
                <Field label="Max jobs per day">
                  <input type="number" min="1" value={profile.availability?.max_jobs_per_day || 3}
                    onChange={(e) => updAvail("max_jobs_per_day", parseInt(e.target.value))} className="input" data-testid="max-jobs-input" />
                </Field>
                <button onClick={saveProfile} disabled={saving} className="mt-4 px-6 py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors inline-flex items-center gap-2 disabled:opacity-50" data-testid="save-availability-btn">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save daily limit
                </button>
              </div>
            </div>
          )}

          {active === "earnings" && (
            <div className="space-y-6" data-testid="earnings">
              <div className="grid sm:grid-cols-3 gap-4">
                <Stat label="Total earnings" value={`£${stats.total_earnings?.toFixed(2)}`} />
                <Stat label="Available balance" value={`£${stats.available_balance?.toFixed(2)}`} />
                <Stat label="Completed jobs" value={stats.completed_jobs} />
              </div>
              <div className="p-4 rounded-2xl border border-border bg-card text-sm max-w-2xl" data-testid="earnings-payout-note">
                <p className="font-semibold">How you get paid</p>
                <p className="text-muted-foreground mt-1">
                  Your earnings are sent automatically to your connected Stripe account after each
                  completed job — there's no withdrawal request to make. Set up or check your payout
                  account in the <button onClick={() => setActive("payouts")} className="text-accent font-medium underline" data-testid="earnings-goto-payouts">Payouts</button> tab.
                </p>
              </div>
            </div>
          )}

          {active === "pricing" && (
            <div className="max-w-3xl" data-testid="cleaner-pricing-tab">
              <h3 className="font-display font-semibold mb-1">Your prices</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Each service is priced its own way. Changes apply to new bookings straight away.
              </p>
              <ServicePricingEditor services={profile.services || []} pricing={pricingDraft}
                onChange={setPricingDraft} showSave />
            </div>
          )}

          {active === "quotes" && (
            <div className="max-w-3xl" data-testid="cleaner-quotes-tab"><QuotesPanel role="cleaner" /></div>
          )}

          {active === "payouts" && (
            <div data-testid="cleaner-payouts-tab"><PayoutsPanel /></div>
          )}

          {active === "reviews" && (
            <div className="space-y-4" data-testid="cleaner-reviews">
              <CleanerReviews cleanerId={profile.cleaner_id} />
            </div>
          )}
        </>
      )}
      <style>{`.input{width:100%;margin-top:0.25rem;padding:0.625rem 0.75rem;border-radius:0.75rem;border:1px solid hsl(var(--border));background:hsl(var(--background));outline:none}.input:focus{border-color:hsl(var(--accent))}`}</style>
    </DashboardShell>
  );
}

function CleanerReviews({ cleanerId }) {
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get(`/reviews/cleaner/${cleanerId}`).then((r) => setReviews(r.data.reviews)); }, [cleanerId]);
  if (reviews.length === 0) return <p className="text-muted-foreground py-10 text-center">No reviews yet.</p>;
  return reviews.map((r) => (
    <div key={r.review_id} className="bg-card rounded-2xl border border-primary/5 p-5">
      <div className="flex justify-between"><span className="font-medium">{r.customer_name}</span><span className="inline-flex items-center gap-1"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {r.rating}</span></div>
      <p className="text-muted-foreground mt-1">{r.text}</p>
    </div>
  ));
}
function Stat({ label, value }) {
  return <div className="bg-card rounded-2xl border border-primary/5 p-6"><p className="font-display text-3xl font-semibold">{value}</p><p className="text-sm text-muted-foreground mt-1">{label}</p></div>;
}
function Field({ label, children }) {
  return <div><label className="text-sm font-medium">{label}</label>{children}</div>;
}
